import { createTheme } from "@mui/material/styles";

/**
 * Custom styles to be apply to all components within the app
 * @returns mui theme object to be reference in MUI ThemeProvider component
 */

const getCustomMuiTheme = (mode) => {
  let theme = {
    palette: {
      mode,
      ...(mode === "light"
        ? {
            //darkest purple
            primary: {
              main: "#2E117F",
            },
            //dark purple
            primaryLight: {
              main: "#5315BC",
            },
            //pink
            secondary: {
              main: "#C31CF3",
            },
            //purple
            focused: {
              main: "#9B57EC",
            },
            //blue
            info: {
              main: "#1E7DFF",
            },
            //grey
            slate: {
              main: "#747474",
            },
            //light grey
            grain: {
              main: "#EAEAEA",
            },
            //green
            success: {
              main: "#028931",
            },
            //red
            error: {
              main: "#CE0C14",
            },
            //background color
            background: {
              main: "#f4f2f8",
            },
            //input background
            inputBackground: {
              main: "#fff",
            },
            tooltipBackground: {
              main: "#f0ecec",
            },
            //load wheel color
            tertiary: {
              main: "#000",
            },
          }
        : {
            // temp colors from light theme start here

            //dark purple
            primaryLight: {
              main: "#5315BC",
            },
            //purple
            focused: {
              main: "#9B57EC",
            },
            //blue
            info: {
              main: "#1E7DFF",
            },
            //grey
            slate: {
              main: "#747474",
            },
            //light grey
            grain: {
              main: "#EAEAEA",
            },
            //green
            success: {
              main: "#028931",
            },
            //red
            error: {
              main: "#CE0C14",
            },
            //input background
            inputBackground: {
              main: "#fff",
            },
            tooltipBackground: {
              main: "#f0ecec",
            },

            // actual dark theme colors start here
            // white
            primary: {
              main: "#FFF",
            },
            // pink
            secondary: {
              main: "#d938fb",
            },
            // dark purple
            background: {
              main: "#120739",
            },
            tertiary: {
              main: "#fff",
            },
          }),
    },
  };

  theme = {
    ...theme,
    typography: {
      fontFamily: "Poppins, sans-serif",
      h1: {
        fontSize: "1.46475rem",
        color: theme.palette.primary.main,
        fontWeight: 1100,
        "@media (max-width:743px) and (min-width:300px)": {
          fontSize: "1.25rem",
        },
      },
      h2: {
        fontSize: "1.17225rem",
        fontWeight: 1100,
      },
      h3: {
        fontSize: "0.9375rem",
        fontWeight: 950,
      },
      h4: {
        fontSize: "0.85rem",
        fontWeight: 500,
      },
      body1: {
        fontSize: "0.75rem",
        lineHeight: 1,
      },
      body2: {
        fontSize: "0.7rem",
      },
      subtitle1: {
        color: theme.palette.primary.main,
        fontSize: "1rem",
        fontWeight: 500,
        "@media (max-width:1280px) and (min-width:744px)": {
          fontSize: "0.85rem",
        },
        "@media (max-width:743px) and (min-width:300px)": {
          fontSize: "0.80rem",
        },
      },
      subtitle2: {
        fontSize: "1rem",
        fontStyle: "italic",
        "@media (max-width:1280px) and (min-width:744px)": {
          fontSize: "0.85rem",
        },
        "@media (max-width:743px) and (min-width:300px)": {
          fontSize: "0.80rem",
        },
      },
      flippyTitle: {
        fontFamily: "Poppins, sans-serif",
        fontWeight: 500,
        fontSize: "1.1rem",
        "@media (max-width:1280px) and (min-width:744px)": {
          fontSize: "0.825rem",
        },
        "@media (max-width:743px) and (min-width:300px)": {
          fontSize: "0.75rem",
        },
      },
      flippySubTitle: {
        fontFamily: "Poppins, sans-serif",
        fontSize: "0.75rem",
        "@media (max-width:1280px) and (min-width:744px)": {
          fontSize: "0.72rem",
        },
        "@media (max-width:743px) and (min-width:300px)": {
          fontSize: "0.7rem",
        },
      },
      error: {
        color: theme.palette.error.main,
        fontSize: ".75rem",
        fontFamily: "Poppins, sans-serif",
      },
      error2: {
        color: theme.palette.error.main,
        fontSize: "1.5rem",
        fontFamily: "Poppins, sans-serif",
      },
      success: {
        color: theme.palette.success.main,
        fontSize: "1rem",
        fontFamily: "Poppins, sans-serif",
      },
      success2: {
        color: theme.palette.success.main,
        fontSize: "1.5rem",
        fontFamily: "Poppins, sans-serif",
      },
      overSize: {
        fontSize: "5rem",
        fontWeight: 600,
      },
      overSize2: {
        fontSize: "3rem",
        fontWeight: 600,
        fontFamily: "Poppins, sans-serif",
      },
    },
    components: {
      MuiLink: {
        styleOverrides: {
          root: {
            "&:hover:not(.Mui-disabled)": {
              cursor: "pointer",
            },
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            fontSize: "1rem",
            color: theme.palette.primary.main,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: theme.palette.tooltipBackground.main,
            color: theme.palette.primary.main,
          },
          arrow: {
            color: theme.palette.tooltipBackground.main,
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            boxShadow: "none",
            marginBottom: "1rem",
            width: "100%",
            color: theme.palette.primary.main,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            backgroundColor: theme.palette.inputBackground.main,
            borderRadius: "10px",
            boxShadow: "-5px 5px 6px 1px #E6E6E6",
            width: "92%",
          },
          option: {
            color: theme.palette.primary.main,
            fontSize: "1.2rem",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            fontSize: "1rem",
            fontWeight: "bolder",
            minWidth: "0",
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: theme.palette.primary.main,
            fontSize: "1rem",
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            color: theme.palette.primary.main,
            fontSize: "1rem",
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            display: "flex",
            flexDirection: "column",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            color: theme.palette.primary.main,
            fontSize: "1.2rem",
          },
          notchedOutline: {
            borderColor: theme.palette.primary.main,
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          root: {
            color: theme.palette.primary.main,
            fontSize: "1.2rem",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            color: theme.palette.primary.main,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            color: theme.palette.primary.main,
            fontSize: "2rem",
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            color: theme.palette.primary.main,
            transform: "scale(1.5)",
            "&:hover:not(.Mui-disabled)": {
              cursor: "pointer",
            },
          },
        },
      },
      MuiTreeItem: {
        styleOverrides: {
          content: {
            padding: "8px",
          },
        },
      },
    },
  };
  const customTheme = createTheme(theme);

  return customTheme;
};

export default getCustomMuiTheme;
